import {
  AndroidBiometryStrength,
  BiometricAuth,
  BiometryType,
  type CheckBiometryResult,
} from "@aparajita/capacitor-biometric-auth"
import { Capacitor } from "@capacitor/core"

export interface BiometricStatus {
  isAvailable: boolean
  biometryType: BiometryType
  biometryTypes: BiometryType[]
  reason: string
  strongBiometryIsAvailable: boolean
  deviceIsSecure: boolean
}

export interface BiometricAuthResult {
  success: boolean
  error?: string
}

class BiometricService {
  private isNative = Capacitor.isNativePlatform()

  /**
   * Check if biometric authentication is available on the device
   */
  async checkBiometryAvailability(): Promise<BiometricStatus> {
    if (!this.isNative) {
      return {
        isAvailable: false,
        biometryType: BiometryType.none,
        biometryTypes: [],
        reason: "Biometric authentication is only available on mobile devices",
        strongBiometryIsAvailable: false,
        deviceIsSecure: false,
      }
    }

    try {
      const result: CheckBiometryResult = await BiometricAuth.checkBiometry()

      return {
        isAvailable: result.isAvailable,
        biometryType: result.biometryType,
        biometryTypes: result.biometryTypes,
        reason: result.reason || "",
        strongBiometryIsAvailable: result.strongBiometryIsAvailable,
        deviceIsSecure: result.deviceIsSecure,
      }
    } catch (error: any) {
      return {
        isAvailable: false,
        biometryType: BiometryType.none,
        biometryTypes: [],
        reason: error.message || "Failed to check biometry availability",
        strongBiometryIsAvailable: false,
        deviceIsSecure: false,
      }
    }
  }

  /**
   * Get a human-readable name for the biometry type
   */
  getBiometryTypeName(type: BiometryType): string {
    switch (type) {
      case BiometryType.touchId:
        return "Touch ID"
      case BiometryType.faceId:
        return "Face ID"
      case BiometryType.fingerprintAuthentication:
        return "Fingerprint"
      case BiometryType.faceAuthentication:
        return "Face Recognition"
      case BiometryType.irisAuthentication:
        return "Iris Scanner"
      default:
        return "Biometric"
    }
  }

  /**
   * Authenticate the user using biometrics
   */
  async authenticate(reason?: string): Promise<BiometricAuthResult> {
    if (!this.isNative) {
      return {
        success: false,
        error: "Biometric authentication is only available on mobile devices",
      }
    }

    try {
      // Check if biometrics are available first
      const status = await this.checkBiometryAvailability()
      if (!status.isAvailable) {
        return {
          success: false,
          error: status.reason || "Biometric authentication is not available",
        }
      }

      const biometryName = this.getBiometryTypeName(status.biometryType)

      // Perform authentication
      await BiometricAuth.authenticate({
        reason: reason || `Unlock Fellespotten with ${biometryName}`,
        cancelTitle: "Cancel",
        iosFallbackTitle: "Use Passcode",
        androidTitle: "Biometric Login",
        androidSubtitle: "Use your fingerprint or face to unlock",
        androidConfirmationRequired: false,
        // Allow both strong and weak biometrics for better device support
        androidBiometryStrength: AndroidBiometryStrength.weak,
        allowDeviceCredential: true, // Allow PIN/pattern/password as fallback
      })

      return { success: true }
    } catch (error: any) {
      // User cancelled or authentication failed
      const errorMessage = error.message || "Authentication failed"

      // Check for specific error cases
      if (errorMessage.includes("cancel") || errorMessage.includes("Cancel")) {
        return {
          success: false,
          error: "Authentication cancelled",
        }
      }

      return {
        success: false,
        error: errorMessage,
      }
    }
  }

  /**
   * Check if the device has any biometrics enrolled
   */
  async hasBiometricsEnrolled(): Promise<boolean> {
    const status = await this.checkBiometryAvailability()
    return status.isAvailable && status.biometryType !== BiometryType.none
  }
}

export const biometricService = new BiometricService()
export { BiometryType }
export default biometricService
